import { useCallback, useEffect, useSyncExternalStore } from 'react'

/**
 * Claro, oscuro o lo que diga el teléfono. Es una preferencia de pantalla y
 * por dispositivo, como mostrar desayuno y merienda: no se sincroniza.
 *
 * `public/tema.js` hace lo mismo antes de que cargue React, para que la app
 * no abra en blanco y salte a oscuro. Si cambian la clave o los colores, hay
 * que cambiarlos en los dos sitios.
 */
export type Tema = 'sistema' | 'claro' | 'oscuro'

export const TEMAS: readonly Tema[] = ['sistema', 'claro', 'oscuro']

const TEMA_KEY = 'recetario:tema'
const COLOR_BARRA = { claro: '#faf6f0', oscuro: '#1c1814' } as const

const listeners = new Set<() => void>()

function leer(): Tema {
  try {
    const valor = localStorage.getItem(TEMA_KEY)
    return valor === 'claro' || valor === 'oscuro' ? valor : 'sistema'
  } catch {
    return 'sistema'
  }
}

function sistemaOscuro(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** Pone o quita la clase `dark` y el color de la barra del navegador. */
export function aplicarTema(tema: Tema): void {
  const oscuro = tema === 'oscuro' || (tema === 'sistema' && sistemaOscuro())
  document.documentElement.classList.toggle('dark', oscuro)
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', oscuro ? COLOR_BARRA.oscuro : COLOR_BARRA.claro)
}

function suscribir(listener: () => void): () => void {
  listeners.add(listener)
  const onStorage = (event: StorageEvent): void => {
    if (event.key === TEMA_KEY) listener()
  }
  window.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    window.removeEventListener('storage', onStorage)
  }
}

export function useTema(): { tema: Tema; elegir: (tema: Tema) => void } {
  const tema = useSyncExternalStore(suscribir, leer, () => 'sistema' as Tema)

  // Con "sistema", seguir al teléfono cuando cambia solo al anochecer.
  useEffect(() => {
    aplicarTema(tema)
    if (tema !== 'sistema' || typeof window.matchMedia !== 'function') return
    const consulta = window.matchMedia('(prefers-color-scheme: dark)')
    const onCambio = (): void => aplicarTema('sistema')
    consulta.addEventListener('change', onCambio)
    return () => consulta.removeEventListener('change', onCambio)
  }, [tema])

  const elegir = useCallback((nuevo: Tema) => {
    try {
      localStorage.setItem(TEMA_KEY, nuevo)
    } catch {
      // Sin almacenamiento, el tema vale para esta sesión y no se recuerda.
      aplicarTema(nuevo)
    }
    for (const listener of listeners) listener()
  }, [])

  return { tema, elegir }
}
